import { useState, useEffect } from 'react'
import { Mode } from '.'
import fuzzy from 'fuzzaldrin-plus'
import { get } from 'lodash'
import { IBuildingDetails, IFloorDetails, IPortfolioRegion, IPortfolioCity } from '@api'

export enum SearchItemType {
  CITY,
  BUILDING,
  FLOOR,
}

type BaseSearchItem<T, V> = {
  hash: string
  type: T
  item: V
}

type CitySearchItem = BaseSearchItem<SearchItemType.CITY, IPortfolioCity>
type BuildingSearchItem = BaseSearchItem<SearchItemType.BUILDING, IBuildingDetails>
type FloorSearchItem = BaseSearchItem<SearchItemType.FLOOR, IFloorDetails>
export type SearchItem = CitySearchItem | BuildingSearchItem | FloorSearchItem

export default function useSearch(
  regions: IPortfolioRegion[],
  mode: Mode
): {
  query: string
  items: SearchItem[]
  run: (query: string) => void
} {
  const [searchItems, setSearchItems] = useState<SearchItem[]>([])
  const [query, setQuery] = useState('')

  useEffect(() => {
    const _searchItems: SearchItem[] = []
    regions.forEach(({ cities }) => {
      cities.forEach((city) => {
        const state = get(city, 'buildings[0].state')
        _searchItems.push({
          hash: `${city.name} ${state}`,
          type: SearchItemType.CITY,
          item: city,
        })

        if (city.buildings) {
          if (mode !== Mode.MULTI_REGION_CITY) {
            city.buildings.forEach((building) => {
              const address = building.address
              _searchItems.push({
                hash: `${building.name} ${address}`,
                type: SearchItemType.BUILDING,
                item: building,
              })

              if ((mode === Mode.SINGLE_FLOOR || mode === Mode.MULTI_FLOOR) && building.floors) {
                const address = building.address
                building.floors.forEach((floor) => {
                  _searchItems.push({
                    hash: `${floor.name} ${building.name} ${address}`,
                    type: SearchItemType.FLOOR,
                    item: floor,
                  })
                })
              }
            })
          }
        }
      })
    })
    setSearchItems(_searchItems)
  }, [regions, mode])

  return {
    query,
    items: query ? fuzzy.filter(searchItems, query, { key: 'hash' }).slice(0, 7) : [],
    run: (newQuery) => setQuery(newQuery),
  }
}
